import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Info,
  AlertTriangle,
  Calendar,
  FileText,
  PartyPopper,
  GraduationCap,
  Megaphone,
  Eye,
  Clock,
  Pin,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import EnhancedCommentSection from "@/components/ui/enhanced-comment-section";
import announcementService from "@/services/announcement.service";
import { formatTimeAgo, formatDate } from "@/lib/utils";

const typeIcons = {
  info: Info,
  urgente: AlertTriangle,
  evenement: Calendar,
  procedure: FileText,
  celebration: PartyPopper,
  formation: GraduationCap,
  autre: Megaphone,
};

const typeColors = {
  info: {
    bg: "bg-blue-50/50 hover:bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-100",
  },
  urgente: {
    bg: "bg-red-50/50 hover:bg-red-50",
    text: "text-red-700",
    border: "border-red-100",
  },
  evenement: {
    bg: "bg-purple-50/50 hover:bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-100",
  },
  procedure: {
    bg: "bg-slate-50/50 hover:bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-100",
  },
  celebration: {
    bg: "bg-pink-50/50 hover:bg-pink-50",
    text: "text-pink-700",
    border: "border-pink-100",
  },
  formation: {
    bg: "bg-emerald-50/50 hover:bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-100",
  },
  autre: {
    bg: "bg-orange-50/50 hover:bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-100",
  },
};

const priorityColors = {
  normale: {
    bg: "bg-blue-100/50 text-blue-700 border-blue-200/50",
  },
  importante: {
    bg: "bg-amber-100/50 text-amber-700 border-amber-200/50",
  },
  urgente: {
    bg: "bg-red-100/50 text-red-700 border-red-200/50",
  },
};

const EnhancedAnnouncementCard = ({ announcement }) => {
  const { user } = useAuth();
  const TypeIcon = typeIcons[announcement.type_annonce] || Megaphone;
  const colors = typeColors[announcement.type_annonce];
  const isPinned = announcement.priorite === "urgente";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ scale: 1.01, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
      className="group relative max-w-2xl mx-auto"
    >
      <Card
        className={`transition-all duration-300 border ${colors.border} shadow-lg hover:shadow-xl overflow-hidden`}
      >
        {isPinned && (
          <div className="absolute top-4 right-16 rounded-full bg-red-500/10 p-2">
            <Pin className="h-4 w-4 text-red-500" />
          </div>
        )}

        <div
          className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300 ${colors.bg}`}
        />

        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-start space-x-4">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.5 }}
              className={`p-4 rounded-full shadow-inner ${colors.bg} ${colors.text}`}
            >
              <TypeIcon className="h-6 w-6" />
            </motion.div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge
                  variant="outline"
                  className={`text-xs ${colors.bg} ${colors.text} border-current`}
                >
                  {announcement.type_annonce}
                </Badge>
                <Badge
                  variant="secondary"
                  className={`text-xs ${
                    priorityColors[announcement.priorite]?.bg
                  }`}
                >
                  {announcement.priorite}
                </Badge>
                {!announcement.isRead && (
                  <Badge
                    variant="destructive"
                    className="text-xs animate-pulse"
                  >
                    Nouveau
                  </Badge>
                )}
              </div>

              <CardTitle className="text-xl font-bold leading-tight text-gray-900 mb-2">
                {announcement.titre}
              </CardTitle>

              <p className="text-gray-600 leading-relaxed text-sm">
                {announcement.contenu}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0 relative z-10">
          <Separator className="mb-4" />

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={announcement.publieur?.photo_profil} />
                      <AvatarFallback>
                        {announcement.publieur?.prenom?.[0]}
                        {announcement.publieur?.nom?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">
                      {announcement.publieur?.prenom}{" "}
                      {announcement.publieur?.nom}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Publié par</TooltipContent>
              </Tooltip>

              <Separator orientation="vertical" className="h-5" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{formatTimeAgo(announcement.date_publication)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Date de publication</TooltipContent>
              </Tooltip>
            </div>

            <div className="flex items-center space-x-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="flex items-center space-x-1"
                  >
                    <Eye className="h-4 w-4" />
                    <span>{announcement._count?.lectures || 0}</span>
                  </motion.div>
                </TooltipTrigger>
                <TooltipContent>Vues</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {announcement.date_expiration && (
            <div className="mt-4 flex items-center space-x-2 text-sm text-amber-600 bg-amber-50/50 rounded-md p-2">
              <Calendar className="h-4 w-4" />
              <span>Expire le {formatDate(announcement.date_expiration)}</span>
            </div>
          )}
        </CardContent>

        {/* Enhanced Comment Section */}
        <EnhancedCommentSection
          announcementId={announcement.id}
          initialCommentCount={announcement._count?.commentaires || 0}
        />
      </Card>
    </motion.div>
  );
};

const AnnouncementWithCommentsDemo = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await announcementService.getAnnouncements({
          page: 1,
          limit: 3,
          actif: true,
          sortBy: "date_publication",
          sortOrder: "desc",
        });
        setAnnouncements(response.data.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement des annonces:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600">Chargement des annonces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            📢 Annonces avec Commentaires
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Découvrez les dernières annonces de l'entreprise et participez aux
            discussions avec vos collègues dans un environnement professionnel
            et moderne.
          </p>
        </div>

        {/* Announcements */}
        <div className="space-y-8">
          {announcements.map((announcement, index) => (
            <motion.div
              key={announcement.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <EnhancedAnnouncementCard announcement={announcement} />
            </motion.div>
          ))}
        </div>

        {announcements.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aucune annonce disponible
            </h3>
            <p className="text-gray-500">
              Les nouvelles annonces apparaîtront ici.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementWithCommentsDemo;
